import { z } from 'zod';
import { AddressSchema } from '@/schemas/models/address';
import { ErrorResponseSchema } from '@/schemas/common/base';

/**
 * POST /api/addresses request schema
 * Creates a new address for the authenticated user
 *
 * The optional fields accept `null` as "absent". Their columns are nullable, so
 * a caller that builds the request by spreading a saved address sends `null`
 * for an empty line2 or name -- /account/addresses seeds its form from the
 * default address that way, and every seller whose default had no line2 got
 * "expected string, received null" and could not add a second address.
 */
export const CreateAddressRequestSchema = z.object({
  /** Full name associated with address (optional) */
  fullName: z.string().max(255).nullish(),
  /** Shipping name (optional, can differ from fullName) */
  shippingName: z.string().max(255).nullish(),
  /** Country ID - accepts string or number, coerces to positive number */
  countryId: z.coerce.number().positive('Valid country is required'),
  /** City/locality */
  locality: z.string().min(2, 'City must be at least 2 characters').max(255),
  /** State/region/province */
  region: z.string().min(2, 'State/Region must be at least 2 characters').max(255),
  /** Address line 1 (street address) */
  line1: z.string().min(4, 'Address line 1 must be at least 4 characters').max(255),
  /** Address line 2 (apartment, suite, etc.) - optional */
  line2: z.string().max(255).nullish(),
  /** Postal/ZIP code */
  zipCode: z.string().min(4, 'ZIP/Postal code must be at least 4 characters').max(20),
  /** Longitude coordinate (optional) - accepts string or number */
  longitude: z.union([z.string(), z.number()]).nullish(),
  /** Latitude coordinate (optional) - accepts string or number */
  latitude: z.union([z.string(), z.number()]).nullish(),
  /** Whether this should be set as the default address (optional) - accepts boolean or 0/1 */
  isDefault: z.union([z.boolean(), z.number()]).optional(),
});

/**
 * POST /api/addresses response schema
 * Returns the created address on success
 */
export const CreateAddressResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    address: AddressSchema,
  }),
  ErrorResponseSchema,
]);

export type CreateAddressRequest = z.infer<typeof CreateAddressRequestSchema>;
export type CreateAddressResponse = z.infer<typeof CreateAddressResponseSchema>;
